import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, usePermissions } from '@/contexts/AuthContext';
import { featureFlags } from '@/config/featureFlags';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePage?: string; // e.g., 'people', 'squad', 'capacity'
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requirePage }) => {
  const { isAuthenticated, isLoading, ssoAttempted, login, userPermissions } = useAuth();
  const { canNavigateTo } = usePermissions();
  const loginAttempted = useRef(false);

  useEffect(() => {
    // Only attempt login if SSO is enabled
    if (!featureFlags.ssoEnabled) {
      return; // Skip login in dev mode
    }

    // If SSO was attempted and user is still not authenticated, redirect immediately
    if (ssoAttempted && !isLoading && !isAuthenticated && !loginAttempted.current) {
      loginAttempted.current = true;

      // Save current URL so we can restore it after the OAuth redirect completes
      sessionStorage.setItem('alloc.redirectStartPage', window.location.href);

      login().catch((error) => {
        console.error('Login error:', error);
      });
    }
  }, [ssoAttempted, isLoading, isAuthenticated, login]);

  // Dev Mode (SSO disabled): Always render content
  if (!featureFlags.ssoEnabled) {
    return <>{children}</>;
  }

  // SSO Mode: Show loading state while checking authentication or attempting SSO
  if (isLoading || !ssoAttempted) {
    return (
      <div className="page-loading">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner"></div>
          <p>Authenticating...</p>
        </div>
      </div>
    );
  }

  // If not authenticated after SSO attempt, keep showing loading while auto-redirecting
  if (!isAuthenticated) {
    return (
      <div className="page-loading">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner"></div>
          <p>Authenticating...</p>
        </div>
      </div>
    );
  }

  // User is authenticated
  // WAIT for permissions to load before checking page access
  // This prevents false redirects to /forbidden during initial load
  if (isAuthenticated && requirePage && !userPermissions?.permissions) {
    return (
      <div className="page-loading">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner"></div>
          <p>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Check if page navigation permission is required and granted
  if (requirePage && !canNavigateTo(requirePage)) {
    return <Navigate to="/forbidden" replace />;
  }

  // User is authenticated and has permission, render the protected content
  return <>{children}</>;
};
